// Core imports
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';

// Component imports
import { Row, Card, Checkbox, Col, Divider, Form, Input, Button, Switch, Space, Radio } from 'antd';
import { RiDeleteBin6Line } from 'react-icons/ri';

import { NdeMixedIcon } from '../Icons/NdeMixedIcon';
import { NdeDistressingIcon } from '../Icons/NdeDistressingIcon';
import { NdeNeutralIcon } from '../Icons/NdeNeutralIcon';
import { NdeCalmingIcon } from '../Icons/NdeCalmingIcon';

// Misc imports
import { NAV_RELATIVE_NDES_ADVANCED_DETAILS, NAV_RELATIVE_NDES_BASIC_DETAILS, NDE_TYPES, PREVIEW_MODES, STORAGE_KEYS, ENVIRONMENTS } from '../../constants';

const BasicInformationTab = props => {
  const { wrapperCol, mode, onSubmitSuccess, onStepChange, initialValues, onDelete } = props;
  const location = useLocation();
  const { pathname } = location;
  const [form] = Form.useForm();
  const [, updateState] = useState(); // Hook to force a component re-render

  // On mount
  useEffect(() => {
    form.resetFields();
    updateState({}); // Trigger re-render so conditional fields show based on initial values

    return () => {
      onStepChange(form.getFieldsValue(true));
    };
  }, [pathname]);

  return (
    <Form
      layout="vertical"
      name="basicInformationForm"
      form={form}
      initialValues={initialValues || JSON.parse(window.localStorage.getItem(STORAGE_KEYS.NDE_TO_CREATE)) || {}}
      onFinish={(values) => onSubmitSuccess(values)} // Let parent form group handle saving based on edit/create mode
      onFieldsChange={(_, allFields) => updateState(allFields)}
      scrollToFirstError
      className={mode === PREVIEW_MODES.VIEW ? 'nde-form-viewonly' : null}
    >
      <Row>
        <Col {...wrapperCol}>
          <Row className="mb-16">
            <Col lg={20} span={24} className="mb-24">
              <h3>
                Type of experience
              </h3>
              <p className="p1-body mb-0">
                Which feeling do you associate your experience with?
              </p>
            </Col>

            <Col span={24}>
              <Form.Item
                name="type"
                rules={[{
                  required: true,
                  message: 'Please choose the feeling most associated with your NDE.'
                }]}
              >
                <Radio.Group
                  options={[
                    {
                      label: (
                        <Space size={0} direction="vertical">
                          <NdeCalmingIcon style={{ fontSize: 52 }} />
                          Peaceful
                        </Space>
                      ),
                      value: NDE_TYPES.PEACEFUL
                    },
                    {
                      label: (
                        <Space size={0} direction="vertical">
                          <NdeDistressingIcon style={{ fontSize: 52 }} />
                          Distressing
                        </Space>
                      ),
                      value: NDE_TYPES.DISTRESSING
                    },
                    {
                      label: (
                        <Space size={0} direction="vertical">
                          <NdeNeutralIcon style={{ fontSize: 52 }} />
                          Neutral
                        </Space>
                      ),
                      value: NDE_TYPES.NEUTRAL
                    },
                    {
                      label: (
                        <Space size={0} direction="vertical">
                          <NdeMixedIcon style={{ fontSize: 52 }} />
                          Mixed
                        </Space>
                      ),
                      value: NDE_TYPES.MIXED
                    }
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                  className="radio-group-cards radio-group-cards-stretch"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col lg={20} span={24} className="mb-24">
              <h5 className="mb-4">
                Where did your experience take place?
              </h5>
              <p className="p1-body mb-0">
                Choose all that apply.
              </p>
            </Col>

            <Col span={24} className="mb-24">
              <Form.Item
                name="environment"
                rules={[{
                  required: true,
                  message: 'Please choose at least one option.'
                }]}
              >
                <Checkbox.Group
                  className="checkbox-group-cards checkbox-group-cards-stretch"
                  options={[
                    { label: 'Earth', value: ENVIRONMENTS.EARTH },
                    { label: 'A place of love', value: ENVIRONMENTS.PLACE_OF_LOVE },
                    { label: 'A place of suffering', value: ENVIRONMENTS.PLACE_OF_SUFFERING },
                    { label: 'A void', value: ENVIRONMENTS.VOID },
                    { label: 'Other', value: ENVIRONMENTS.OTHER }
                  ]}
                >
                </Checkbox.Group>
              </Form.Item>

              {
                form.getFieldValue('environment') === ENVIRONMENTS.OTHER ? (
                  <Form.Item name="environmentText">
                    <Input size="large" placeholder="Please describe (optional)" />
                  </Form.Item>
                ) : null
              }
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <Divider className="mt-12 mb-42" />
            </Col>
          </Row>

          <Row>
            <Col lg={20} span={24} className="mb-24">
              <h3>
                Share your experience
              </h3>
              <p className="p1-body mb-0">
                Here you can enter your experience in plain text. Explain your experience in detail or write down whatever you feel. In the <Link to={pathname.replace(NAV_RELATIVE_NDES_BASIC_DETAILS, NAV_RELATIVE_NDES_ADVANCED_DETAILS)}>Advanced Details</Link> tab, you will be able to add more specific information if you want.
              </p>
            </Col>

            <Col span={24}>
              <Form.Item
                name="text"
                rules={[{
                  required: true,
                  message: 'Please enter your experience before proceeding.'
                }]}
              >
                <Input.TextArea size="large" rows={12} placeholder="Tip: You can just copy and paste your experience if you already shared it somewhere else!" />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <Divider className="mt-32 mb-42" />
            </Col>
          </Row>

          <Row>
            <Col lg={20} span={24} className="mb-24">
              <h3>
                Privacy settings
              </h3>
              <p className="p1-body mb-0">
                NDEs are public by default. If you wish to hide your experience from readers (but still assist us with NDE analysis), you can make it private.
              </p>
            </Col>

            <Col span={24}>
              <Card className="border-color-black-40">
                <Row justify="space-between" align="middle">
                  <Col>
                    Make NDE public
                  </Col>
                  <Col>
                    <Form.Item name="isPublic" valuePropName="checked" className="mb-0">
                      <Switch defaultChecked />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {
        mode === PREVIEW_MODES.VIEW ? null : (
          <Row justify={mode === PREVIEW_MODES.EDIT ? 'space-between' : 'end'} align="middle" className="mt-48">
            {
              mode === PREVIEW_MODES.EDIT ? (
                <Col>
                  <Button
                    danger
                    icon={<RiDeleteBin6Line size={14} style={{ marginRight: 4 }} />}
                    onClick={onDelete}
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    Delete NDE
                  </Button>
                </Col>
              ) : null
            }
            <Col>
              <Button type="primary" htmlType="submit" className="bg-black-100 border-color-black-100 hover-bg-black-80 hover-border-color-black-80">
                {
                  mode === PREVIEW_MODES.EDIT ? 'Save' : 'Next step'
                }
              </Button>
            </Col>
          </Row>
        )
      }
    </Form>
  );
};

BasicInformationTab.propTypes = {
  mode: PropTypes.string,
  wrapperCol: PropTypes.object,
  onSubmitSuccess: PropTypes.func,
  onStepChange: PropTypes.func,
  initialValues: PropTypes.object,
  onDelete: PropTypes.func
};

export default BasicInformationTab;
